import React, { useState, lazy, Suspense, useMemo } from 'react';
import { useI18n } from '../i18n/context';
import ClientMain from '../components/ClientMain';
import TabBar from '../components/TabBar';

const ClientExercises = lazy(() => import('../components/ClientExercises'));
const ProfileTab = lazy(() => import('../components/ProfileTab'));
import { Button } from '../components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import LogoutConfirmDialog from '../components/LogoutConfirmDialog';

const ClientDashboard = ({ currentUser, userContext, onLogout, onRoleChange }) => {
  const { t } = useI18n();
  const isTelegramMiniApp = !!(
    typeof window !== 'undefined' && window.Telegram?.WebApp?.initData
  );
  const hideLogout = isTelegramMiniApp;
  const [activeTab, setActiveTab] = useState('main');
  const [logoutConfirmOpen, setLogoutConfirmOpen] = useState(false);

  const tabs = useMemo(
    () => [
      { id: 'main', label: t('nav.home'), icon: 'fas fa-home' },
      { id: 'exercises', label: t('nav.exercises'), icon: 'fas fa-dumbbell' },
    ],
    [t]
  );

  return (
    <div className="client-dashboard flex flex-1 flex-col min-h-0 w-full max-w-[100vw] min-w-0 bg-background overflow-hidden">
      {/* Header - Mobile optimized */}
      <header className="flex-shrink-0 w-full border-b bg-background safe-area-inset-top">
        <div className="flex h-14 min-w-0 items-center justify-between gap-2 px-[var(--space-md)] safe-area-inset-left safe-area-inset-right">
          <div className="flex items-center">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  className="rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ring-offset-background"
                  aria-label={t('nav.profileMenu')}
                >
                  <Avatar className="h-10 w-10">
                    <AvatarImage src={userContext?.user?.avatar || currentUser?.avatar} alt={userContext?.user?.name || currentUser?.name} />
                    <AvatarFallback className="bg-primary text-primary-foreground">
                      <i className="fas fa-user h-4 w-4"></i>
                    </AvatarFallback>
                  </Avatar>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-56">
                <DropdownMenuLabel className="truncate">
                  {userContext?.user?.name || userContext?.user?.nickname || currentUser?.name || currentUser?.nickname || t('nav.profile')}
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={() => {
                    setActiveTab('profile');
                  }}
                >
                  {t('nav.profile')}
                </DropdownMenuItem>
                {!hideLogout && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      onClick={() => setLogoutConfirmOpen(true)}
                      className="text-destructive focus:text-destructive"
                    >
                      {t('dash.logout')}
                    </DropdownMenuItem>
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
          <div className="flex items-center gap-2">
            {userContext?.roles.is_trainer && (
              <Button 
                variant="outline" 
                size="icon"
                onClick={() => onRoleChange('trainer')}
                className="text-blue-600 border-blue-300 active:bg-blue-50 touch-manipulation h-10 w-10"
                title={t('dash.switchToTrainer')}
              >
                <i className="fas fa-user-tie"></i>
              </Button>
            )}
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 min-h-0 min-w-0 w-full overflow-y-auto overflow-x-hidden overscroll-y-contain pb-[var(--app-tab-bar-height)]">
        {activeTab === 'main' && <ClientMain currentUser={currentUser} userContext={userContext} />}
        <Suspense fallback={<div className="flex items-center justify-center p-8"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" /></div>}>
          {activeTab === 'exercises' && <ClientExercises currentUser={currentUser} userContext={userContext} />}
          {activeTab === 'profile' && (
            <ProfileTab
              currentUser={currentUser}
              currentRole="client"
              onRoleChange={onRoleChange}
              userContext={userContext}
              onLogout={onLogout}
            />
          )}
        </Suspense>
      </main>

      {/* Bottom Navigation */}
      <TabBar tabs={tabs} activeTab={activeTab} onTabChange={setActiveTab} />

      <LogoutConfirmDialog
        open={logoutConfirmOpen}
        onOpenChange={setLogoutConfirmOpen}
        onConfirm={() => {
          setLogoutConfirmOpen(false);
          onLogout?.();
        }}
      />
    </div>
  );
};

export default ClientDashboard;
